import { API_BASE } from '../config/api';
import TokenManager from './TokenManager';

// Module-level cache for getCurrentUser (survives HMR better than class statics)
let _userCache = { user: null, timestamp: 0, pending: null };
const CACHE_TTL = 30000; // 30 seconds
// Bumped by clearUserCache() at every auth boundary. A /me answer for an
// earlier generation belongs to a session that has ended: it must not refill
// the cache or clear the next account's token (BH-02-02).
let _cacheGeneration = 0;

class AuthService {
  // Get current authenticated user (cached, deduped)
  static async getCurrentUser() {
    const token = TokenManager.getTokenSync();
    if (!token) {
      _userCache = { user: null, timestamp: 0, pending: null };
      return null;
    }

    // Return cached result if still fresh
    if (_userCache.user && (Date.now() - _userCache.timestamp) < CACHE_TTL) {
      return _userCache.user;
    }

    // Deduplicate concurrent requests — reuse in-flight promise
    if (_userCache.pending) {
      return _userCache.pending;
    }

    const generation = _cacheGeneration;
    const isStale = () => generation !== _cacheGeneration;
    const request = fetch(`${API_BASE}/api/auth/me`, {
      headers: this.getJsonHeaders()
    }).then(async (response) => {
      if (isStale()) return null;
      if (!response.ok) {
        TokenManager.clearToken();
        _userCache = { user: null, timestamp: 0, pending: null };
        return null;
      }
      const data = await response.json();
      if (isStale()) return null;
      const user = data.success ? data.user : null;
      _userCache.user = user;
      _userCache.timestamp = Date.now();
      return user;
    }).catch((error) => {
      console.error('Error fetching current user:', error);
      return null;
    }).finally(() => {
      if (!isStale()) _userCache.pending = null;
    }).then((user) => (
      // Whoever awaited the ended session's request gets the current one's
      // answer, not the previous account's user (or a spurious null).
      isStale() ? AuthService.getCurrentUser() : user
    ));

    _userCache.pending = request;
    return request;
  }

  // Clear the user cache (call on login/logout/signup)
  static clearUserCache() {
    _cacheGeneration += 1;
    _userCache = { user: null, timestamp: 0, pending: null };
  }

  // Check if user is authenticated
  static isAuthenticated() {
    return TokenManager.isAuthenticated();
  }

  // Sign up new user
  static async signUp(userData) {
    const { email, password, confirmPassword, name, dateOfBirth, termsAccepted, betaCode, referralCode } = userData;
    
    // Client-side validation
    if (!email || !password || !confirmPassword || !name) {
      return { success: false, message: 'All fields are required' };
    }

    if (password !== confirmPassword) {
      return { success: false, message: 'Passwords do not match' };
    }

    if (password.length < 8) {
      return { success: false, message: 'Password must be at least 8 characters' };
    }

    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password)) {
      return { success: false, message: 'Password must include uppercase, lowercase, and a number' };
    }

    if (!this.isValidEmail(email)) {
      return { success: false, message: 'Please enter a valid email address' };
    }

    try {
      const response = await fetch(`${API_BASE}/api/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Needed for dual-token refresh cookie
        // beta_code / referral_code carry the invite the client already
        // validated. The server re-validates and claims it — these are the
        // access credential, not a hint (see authRoutes signup gate).
        body: JSON.stringify({
          email,
          password,
          name,
          date_of_birth: dateOfBirth,
          termsAccepted,
          ...(betaCode ? { beta_code: betaCode } : {}),
          ...(referralCode ? { referral_code: referralCode } : {})
        })
      });

      // Handle rate limiting before parsing JSON
      if (response.status === 429) {
        return { success: false, message: 'Too many attempts. Please wait a few minutes and try again.' };
      }

      const data = await response.json();

      if (data.success) {
        // Handle both V1 (token) and V2 (accessToken) response shapes
        TokenManager.storeToken(data.accessToken || data.token);
        this.clearUserCache();
        return { success: true, user: data.user, message: data.message };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, message: 'Network error. Please try again.' };
    }
  }

  // Sign in existing user. remember=false keeps the session browser-lifetime
  // only (see TokenManager.storeToken).
  static async signIn(email, password, remember = true) {
    if (!email || !password) {
      return { success: false, message: 'Email and password are required' };
    }

    try {
      if (TokenManager.isEnabled()) {
        // Dual-token: delegate to TokenManager (handles V2 endpoint + cookie)
        const result = await TokenManager.signIn(email, password, remember);
        if (result.success) {
          this.clearUserCache();
          return { success: true, user: result.user, message: result.message };
        }
        return { success: false, message: result.error || result.message || 'Login failed' };
      }

      // Legacy single-token flow
      const response = await fetch(`${API_BASE}/api/auth/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        // /signin also sets the refresh cookie when the server runs dual
        // tokens; "Remember me" off makes it a session cookie (BH-02-03).
        body: JSON.stringify({ email, password, remember })
      });

      // Handle rate limiting before parsing JSON
      if (response.status === 429) {
        return { success: false, message: 'Too many sign-in attempts. Please wait a few minutes and try again.' };
      }

      const data = await response.json();

      if (data.success) {
        TokenManager.storeToken(data.token, remember);
        this.clearUserCache();
        return { success: true, user: data.user, message: data.message };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Signin error:', error);
      return { success: false, message: 'Network error. Please try again.' };
    }
  }

  // Sign out user
  static async signOut() {
    try {
      if (TokenManager.isEnabled()) {
        // Dual-token: call server to revoke refresh token + clear cookie
        await TokenManager.signOut();
      } else {
        TokenManager.clearToken();
      }
    } catch (error) {
      // Always clear local tokens even if server call fails
      TokenManager.clearToken();
      console.error('Sign out error:', error);
    }
    this.clearUserCache();
    return { success: true, message: 'Signed out successfully!' };
  }

  // Request password reset email
  static async forgotPassword(email) {
    try {
      const response = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (response.status === 429) {
        return { success: false, message: 'Too many attempts. Please wait a few minutes.' };
      }

      const data = await response.json();
      return { success: data.success, message: data.message };
    } catch (error) {
      console.error('Forgot password error:', error);
      return { success: false, message: 'Network error. Please try again.' };
    }
  }

  // Reset password with token
  static async resetPassword(token, newPassword) {
    try {
      const response = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: newPassword })
      });

      if (response.status === 429) {
        return { success: false, message: 'Too many attempts. Please wait a few minutes.' };
      }

      const data = await response.json();
      return { success: data.success, message: data.message };
    } catch (error) {
      console.error('Reset password error:', error);
      return { success: false, message: 'Network error. Please try again.' };
    }
  }

  // Resend email verification
  static async resendVerification() {
    try {
      const response = await fetch(`${API_BASE}/api/auth/resend-verification`, {
        method: 'POST',
        headers: this.getJsonHeaders()
      });

      if (response.status === 429) {
        return { success: false, message: 'Too many attempts. Please wait a few minutes.' };
      }

      const data = await response.json();
      return { success: data.success, message: data.message };
    } catch (error) {
      console.error('Resend verification error:', error);
      return { success: false, message: 'Network error. Please try again.' };
    }
  }

  // Update user profile
  static async updateProfile(updates) {
    if (!TokenManager.getTokenSync()) {
      return { success: false, message: 'Not authenticated' };
    }

    try {
      const response = await fetch(`${API_BASE}/api/auth/profile`, {
        method: 'PUT',
        headers: this.getJsonHeaders(),
        body: JSON.stringify(updates)
      });

      const data = await response.json();

      if (data.success) {
        this.clearUserCache();
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Profile update error:', error);
      return { success: false, message: 'Network error. Please try again.' };
    }
  }


  // Get authorization headers for API requests
  // This is the centralized method for all services to use
  static getAuthHeaders() {
    return TokenManager.getAuthHeaders();
  }

  // Centralized response handler - Graceful variant (no redirect)
  // Use this when you want to handle auth errors gracefully without forcing logout
  // Throws errors that callers can catch and handle appropriately
  static async handleResponse(response) {
    if (response.status === 401) {
      console.warn('AuthService: No auth token or token expired');
      throw new Error('Authentication required');
    }

    if (response.status === 403) {
      // A moderation refusal is told to the content's owner in the server's
      // own words (policy §12.3, C41) — it is the one 403 whose message is
      // meant for them. Every other 403 stays generic.
      const body = await response.json().catch(() => null);
      if (body?.moderated && body.error) throw new Error(body.error);
      console.warn('AuthService: Access denied or token invalid');
      throw new Error('Access denied');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(error.error || error.message || 'Request failed');
    }

    return response.json();
  }

  // Get auth token directly (for services that need just the token)
  static getAuthToken() {
    return TokenManager.getTokenSync();
  }

  // Get headers with Content-Type: application/json included
  // Convenience method to reduce the common pattern of:
  //   { ...AuthService.getAuthHeaders(), 'Content-Type': 'application/json' }
  static getJsonHeaders() {
    return {
      ...this.getAuthHeaders(),
      'Content-Type': 'application/json'
    };
  }

  // Utility functions
  static isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  static generateAvatar(name) {
    const avatars = ['👤', '🧑', '👩', '🧔', '👱', '🧑‍🦱', '👩‍🦱', '🧑‍🦰', '👩‍🦰', '🧑‍🦲', '👩‍🦲'];
    const initials = name.split(' ').map(n => n[0]).join('').toUpperCase();
    return avatars[initials.charCodeAt(0) % avatars.length];
  }

  // Default travel profile — used as the fallback when a user has no stored
  // profile, or for travelers whose profile we can't resolve synchronously
  // (group members other than the current user). Mirrors the signup default in
  // server/routes/authRoutes.js.
  static getDefaultTravelProfile() {
    return {
      energyLevel: 'medium',
      ageGroup: 'adult',
      mobilityLevel: 'full',
      endurance: 'medium',
      preferredPace: 'moderate',
      maxWalkingDistance: 5,
      maxActivitiesPerDay: 4,
      needsFrequentBreaks: false,
      accessibilityNeeds: [],
      medicalConsiderations: []
    };
  }

  // Get travel profile for a user. Reads the real stored `travelProfile` for
  // the current user (from the getCurrentUser cache) so a user's edited
  // mobility/pace/energy preferences actually feed the group-constraint math.
  // For other travelers (no synchronous per-user fetch available) and for users
  // with no stored profile, returns the sensible default. Merged over the
  // default so a partial stored profile never produces undefined fields.
  static getTravelProfile(userId) {
    const cached = _userCache.user;
    // String-coerce the id compare — caller ids can be numeric where the
    // cached user id is a string (same convention as elsewhere in the app).
    if (cached && userId != null && String(cached.id) === String(userId) && cached.travelProfile) {
      return { ...this.getDefaultTravelProfile(), ...cached.travelProfile };
    }
    return this.getDefaultTravelProfile();
  }

  // Calculate group travel constraints based on all travelers
  static calculateGroupTravelConstraints(travelers) {
    if (!travelers || travelers.length === 0) {
      return {
        maxWalkingDistance: 5,
        maxActivitiesPerDay: 4,
        needsFrequentBreaks: false,
        accessibilityNeeds: [],
        groupEnergyLevel: 'medium',
        groupPace: 'moderate'
      };
    }

    // Get travel profiles for all travelers (getTravelProfile always returns a
    // complete profile — real for the current user, default otherwise).
    const profiles = travelers.map(traveler => this.getTravelProfile(traveler.id));

    // Calculate group constraints (most restrictive wins)
    const groupConstraints = {
      maxWalkingDistance: Math.min(...profiles.map(p => p.maxWalkingDistance)),
      maxActivitiesPerDay: Math.min(...profiles.map(p => p.maxActivitiesPerDay)),
      needsFrequentBreaks: profiles.some(p => p.needsFrequentBreaks),
      accessibilityNeeds: [...new Set(profiles.flatMap(p => p.accessibilityNeeds || []))],
      groupEnergyLevel: this.calculateGroupEnergyLevel(profiles),
      groupPace: this.calculateGroupPace(profiles),
      groupAge: this.calculateGroupAge(profiles),
      hasLimitedMobility: profiles.some(p => p.mobilityLevel === 'limited' || p.mobilityLevel === 'assisted')
    };

    return groupConstraints;
  }

  static calculateGroupEnergyLevel(profiles) {
    const energyLevels = profiles.map(p => p.energyLevel);
    
    // If anyone has low energy, group is low energy
    if (energyLevels.includes('low')) return 'low';
    
    // If majority has high energy and no one has low, group is high
    const highCount = energyLevels.filter(e => e === 'high').length;
    if (highCount > profiles.length / 2) return 'high';
    
    return 'medium';
  }

  static calculateGroupPace(profiles) {
    const paces = profiles.map(p => p.preferredPace);
    
    // Most restrictive pace wins
    if (paces.includes('relaxed')) return 'relaxed';
    if (paces.includes('moderate')) return 'moderate';
    return 'fast';
  }

  static calculateGroupAge(profiles) {
    const ageGroups = profiles.map(p => p.ageGroup);
    
    if (ageGroups.includes('senior')) return 'mixed_with_seniors';
    if (ageGroups.includes('young') && ageGroups.includes('adult')) return 'mixed_adult';
    if (ageGroups.every(a => a === 'young')) return 'young';
    if (ageGroups.every(a => a === 'adult')) return 'adult';
    return 'mixed';
  }
}

export default AuthService;